import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import { useAuth } from '../../hooks/useAuth';
import './FavoritesModal.css';

const FavoritesModal = ({ isOpen, onClose, onSelectStock }) => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newStockSymbol, setNewStockSymbol] = useState('');
  const [newStockName, setNewStockName] = useState('');
  const [addingStock, setAddingStock] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadFavorites();
    }
  }, [isOpen]);

  const loadFavorites = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.getFavorites(user?.uid);
      setFavorites(response.favorites || []);
    } catch (err) {
      setError('Failed to load favorites');
      console.error('Error loading favorites:', err);
    } finally {
      setLoading(false);
    }
  };

  const addFavorite = async (e) => {
    e.preventDefault();

    // Stock symbol is required
    if (!newStockSymbol.trim()) {
      setError('Stock symbol is required');
      return;
    }

    setAddingStock(true);
    try {
      console.log(user?.uid);
      const newFavorite = await apiClient.addFavorite(
        newStockSymbol.trim(),
        newStockName.trim() || null,
        user?.uid
      );

      setFavorites(prev => [...prev, newFavorite]);
      setNewStockSymbol('');
      setNewStockName('');
    } catch (err) {
      setError('Failed to add stock to favorites');
      console.error('Error adding favorite:', err);
    } finally {
      setAddingStock(false);
    }
  };

  const removeFavorite = async (symbol) => {
    try {
      await apiClient.removeFavorite(symbol, user?.uid);
      setFavorites(prev => prev.filter(fav => fav.symbol !== symbol));
    } catch (err) {
      setError('Failed to remove stock from favorites');
      console.error('Error removing favorite:', err);
    }
  };

  const handleClose = () => {
    setError(null);
    setNewStockSymbol('');
    setNewStockName('');
    onClose();
  };

  const handleStockSelect = (symbol) => {
    onSelectStock(symbol);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="favorites-modal-overlay" onClick={handleClose}>
      <div className="favorites-modal" onClick={e => e.stopPropagation()}>
        <div className="favorites-modal-header">
          <h2>Favorite Stocks</h2>
          <button className="close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="favorites-modal-content">
          {/* Add New Stock Form */}
          <div className="add-stock-form">
            <h3>Add New Stock</h3>
            <form onSubmit={addFavorite}>
              <div className="form-row">
                <div className="form-group required">
                  <label htmlFor="symbol">Stock Symbol *</label>
                  <input
                    type="text"
                    id="symbol"
                    value={newStockSymbol}
                    onChange={(e) => setNewStockSymbol(e.target.value.toUpperCase())}
                    placeholder="e.g., AAPL, MSFT, GOOGL"
                    required
                  />
                  <small className="field-help">Enter the stock ticker symbol</small>
                </div>
                <div className="form-group">
                  <label htmlFor="name">Company Name</label>
                  <input
                    type="text"
                    id="name"
                    value={newStockName}
                    onChange={(e) => setNewStockName(e.target.value)}
                    placeholder="e.g., Apple Inc."
                  />
                  <small className="field-help">Add company name for better identification</small>
                </div>
              </div>
              <div className="form-actions">
                <button
                  type="submit"
                  className="add-button"
                  disabled={addingStock || !newStockSymbol.trim()}
                >
                  {addingStock ? 'Adding...' : 'Add to Favorites'}
                </button>
                <small className="form-note">
                  * Stock symbol is required. Company name is optional.
                </small>
              </div>
            </form>
          </div>

          {/* Error Display */}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Favorites List */}
          <div className="favorites-list">
            <h3>Your Favorites ({favorites.length})</h3>

            {loading ? (
              <div className="loading">Loading favorites...</div>
            ) : favorites.length === 0 ? (
              <div className="empty-state">
                <p>No favorite stocks yet. Add some stocks to get started!</p>
              </div>
            ) : (
              <div className="favorites-grid">
                {favorites.map((favorite) => (
                  <div key={favorite.symbol} className="favorite-item">
                    <div
                      className="favorite-info clickable"
                      onClick={() => handleStockSelect(favorite.symbol)}
                      title={`Click to view ${favorite.symbol} dashboard`}
                    >
                      <div className="stock-symbol">{favorite.symbol || 'N/A'}</div>
                      {favorite.name && (
                        <div className="stock-name">{favorite.name}</div>
                      )}
                      {favorite.added_at && (
                        <div className="added-date">
                          Added: {new Date(favorite.added_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <button
                      className="remove-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFavorite(favorite.symbol);
                      }}
                      title="Remove from favorites"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FavoritesModal;
